/**
 * DesktopSignInModal — the LARGE in-page sign-in surface for systems outside
 * the catalog (2026-08-18 UX overhaul, phase 6; 2026-08-19 interactive-surface
 * fix). The user signs into the real provider page inside prest0n's own
 * browser without ever leaving the page (or chat) they started from.
 *
 * Reliability doctrine, enforced in layers:
 *   1. BOOT: the modal opens instantly with an honest progressive status;
 *      the desktop session is started with backoff retries. A created
 *      session (POST success) is the proof of life — the old first-frame
 *      gate rode a viewer WebSocket that never served desktop sessions, so
 *      the surface could NEVER reach live. Never a dead surface.
 *   2. INTERACTIVE SURFACE (2026-08-19): the VM's own noVNC page in an
 *      iframe — the exact recipe /desktop's realtime page uses
 *      (/novnc/<wsPort>/vnc.html, view_only=0), full keyboard+mouse, no
 *      bundler involved. The "open the live desktop" button stays as the
 *      own-window option and MINTS the deep link AT CLICK TIME (fresh 5-min
 *      token — a stale link can never be handed out).
 *   3. KEEPALIVE: the surface pings the session while open so the server's
 *      20-minute idle sweep never reaps a slow sign-in (2FA, reset email).
 *   4. COMPLETION is user-attested: "done — I'm signed in" promotes the
 *      custom connection registered → connected (the only writer), then the
 *      connected tile appears at the top of the Integrations page.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PanelModal from './PanelModal';
import { DESKTOP_SIGNIN_Z_INDEX } from '../../../styles/layers';
import panelTheme from '../panelTheme';
import {
  startSignInDesktopSession,
  completeCustomConnection,
  runSignInPilotTask,
  buildDesktopNoVncUrl,
  getDesktopSessionInfo,
  sendDesktopKeepalive,
  checkSignInStatus,
  checkSignInStatusDetailed,
  fetchOverlayGuide,
  navigateDesktopSession,
  pageBelongsToApp,
  resolveKnownSignInDestinationSync,
  openSignInJourney,
  postJourneyControl,
  pushLocalClipboardToDesktop,
  WorkspaceNotReadyError,
  type DesktopSignInSession,
  type DesktopPageKind,
  type OverlayGuide,
  type SignInPilotOutcome,
  type SignInPilotLanding,
} from '../desktopSignIn';
import { announceConnectSignedIn, type ConnectSignInOrigin } from '../connectSignedInEvent';
import { subscribeToJourney, type JourneyDocSnapshot } from '../journeyDoc';
import { isTerminalControl, type ControlState } from '../../../config/journeyProtocol.generated';
import { getAuth } from 'firebase/auth';
import { resolveAccount } from '../../prest0nRuntime/accountResolver';
import DesktopGuidanceOverlay, { type ActiveGuidance } from './DesktopGuidanceOverlay';
import useStageFit from './useStageFit';
import type { WorkspaceBootProgress } from '../../browserService/workspaceBootGate';
import {
  listCustomConnections,
  registerCustomConnection,
  updateCustomConnectionAccess,
  reportDestination,
} from '../ComposioClient';
import { fetchWorkspaceOptionsCached } from '../../workspaceOptionsCache';
import { workspaceLabel } from '../../workspaceIdentity';
import { getRegisteredBrowserSessionBase } from '../../globalTools/tools/browserSessionFrames';
import { mintDeepLinkUrl } from '../../globalTools/tools/browserDeepLinkClient';
import { invalidateConnectedAppsCache } from '../../threadRecommendations/connectedAppsStore';
import { logConnectFunnel } from '../connectPopupFlow';

export interface DesktopSignInModalProps {
  appName: string;
  /** When set, "done — I'm signed in" promotes this custom connection. */
  customSlug?: string;
  /** Scope the custom connection was registered under. Used when the modal
   *  has to register one itself (a missing slug at completion); an access
   *  edit never sends it — the doc's own scope stands (P0 #6). */
  scope?: 'personal' | 'organization';
  /** Reuse an existing desktop session (chat handoff) instead of starting one. */
  sessionId?: string;
  /** Researched official sign-in URL (rides the connect card, 2026-08-19) —
   *  survives reloads, beats the static-map/search fallback. */
  signInUrl?: string;
  /** Explicit workstation target (2026-08-23): the reserved/minted org id
   *  during onboarding — the back-pointer still names the previous
   *  workspace, and the sign-in must land on the ORG's machine. */
  workspaceAccountId?: string;
  /** Language for the surface's code-authored failure lines ({en, es}).
   *  Absent — the browser's own language (D9 copy must never be silently
   *  English for a Spanish-speaking user). */
  lang?: 'en' | 'es';
  /**
   * Where this sign-in came from (2026-09-03). A sign-in started from a chat
   * card carries the thread that asked for it and the question it
   * interrupted; on completion the announcement carries them back so the
   * thread resumes instead of sitting there. Absent on surfaces with no
   * conversation behind them (the Integrations page).
   */
  origin?: ConnectSignInOrigin;
  onConnected?: () => void;
  onClose: () => void;
  /** Test seams — production always uses the defaults. */
  retryDelaysMs?: number[];
  liveWatchdogMs?: number;
  keepaliveIntervalMs?: number;
  verifyPollMs?: number;
  probeEmbedReachable?: (url: string) => Promise<boolean>;
}

/** Watchdog embed probe (2026-08-20): an opaque no-cors fetch resolves when
 *  the noVNC server answers at all and rejects on the blank-iframe failure
 *  classes (route down, TLS, DNS). Environments without fetch answer
 *  reachable — the own-window button stays the escape hatch there. */
async function defaultProbeEmbedReachable(url: string): Promise<boolean> {
  if (typeof fetch !== 'function') return true;
  try {
    await fetch(url, { mode: 'no-cors', cache: 'no-store' });
    return true;
  } catch {
    return false;
  }
}

/** HTTP statuses that mean the destination itself is gone. */
const DESTINATION_GONE_STATUSES = [404, 410];

/**
 * Did this drive end somewhere the destination can be blamed for? TRUE for
 * the off-app redirect class (the Fitbit store repro) and for a landing the
 * workstation classified as gone. The answer only decides whether the
 * surface REPORTS what the VM observed — the server, reading its own journey
 * receipts, decides whether anything is done about it (contract C3).
 */
export function destinationLandedWrong(
  outcome: SignInPilotOutcome,
  landing?: SignInPilotLanding,
): boolean {
  // A drive that ARRIVED at a login form never accuses its destination, even
  // if the landing carries a stale/odd kind: the surface would be showing
  // "sign-in page ready" while the report said the address was dead. The
  // server's own `login_seen` guard refuses such a report anyway — this keeps
  // the surface and what it tells the server from ever disagreeing.
  if (outcome === 'login-form') return false;
  if (outcome === 'wrong-destination') return true;
  return landedOnAGonePage(landing);
}

/** Is the landing itself the not-found class (the honest "that page is gone"
 *  line), as opposed to the generic "couldn't get there" one? */
function landedOnAGonePage(landing?: SignInPilotLanding): boolean {
  if (!landing) return false;
  if (landing.kind === 'notfound') return true;
  return typeof landing.status === 'number' && DESTINATION_GONE_STATUSES.includes(landing.status);
}

/** Code-authored FAILURE copy (D9) — every line {en, es}. */
// copy: desktop_signin_pilot_chrome
const PILOT_NOTFOUND_COPY = {
  en: 'that page is gone \u2014 finding the right one',
  es: 'esa p\u00e1gina ya no existe \u2014 buscando la correcta',
} as const;

/** The ACTIONABLE failure line. It is what a spent gone-page search hands
 *  over to, and what the off-app redirect class shows (that page is not gone,
 *  it is the wrong one), so it carries the same {en, es} obligation as its
 *  sibling above \u2014 it was English-only while it was the pilot's single
 *  failure line (2026-09-02 review). */
// copy: desktop_signin_pilot_chrome
const PILOT_MANUAL_COPY = {
  en: 'couldn\u2019t reach the sign-in page automatically \u2014 use the window above to navigate there',
  es: 'no se pudo llegar a la p\u00e1gina de inicio de sesi\u00f3n \u2014 usa la ventana de arriba para ir a ella',
} as const;

/** No destination at all. Retiring the fabricated `https://www.<key>.com`
 *  rung (contract C8) was right — a live browser must never be pointed at a
 *  guess — but it left this case saying NOTHING: the surface opened, the
 *  pilot had nowhere to drive, and the person watched an empty browser with
 *  no idea what was wanted of them (2026-09-02 review). Code-authored
 *  FAILURE copy (D9), {en, es}. It does not claim a drive that never
 *  happened, which is what the manual line beside it would have said. */
// copy: desktop_signin_pilot_chrome
const PILOT_NO_DESTINATION_COPY = {
  en: 'no verified sign-in address for this one yet \u2014 open it in the window above and Prest0n takes it from there',
  es: 'a\u00fan no hay una direcci\u00f3n de inicio de sesi\u00f3n verificada para esta \u2014 \u00e1brela en la ventana de arriba y Prest0n sigue desde ah\u00ed',
} as const;

/**
 * The surface's own controls (2026-09-03). {en, es} like every other line
 * this file authors; "Prest0n" keeps its capital P (2026-08-22 naming rule).
 */
/**
 * The window, sized from the VIEWPORT ALONE — never from the fitted stage
 * (2026-09-04).
 *
 * Deriving the width from the stage closes a four-term cycle: card width →
 * whether the footer wraps → chrome height → slot height → stage height →
 * stage width → card width. That oscillates at the wrap boundary. So the card
 * is a pure function of the viewport, and the stage is measured inside it.
 *
 * The `220px` term is a chrome ESTIMATE and is cosmetic only: it decides how
 * much matting sits beside the live screen, never how that screen is fitted
 * (which is measured). Getting it wrong costs a little matting and nothing
 * else. Plain vh throughout — no `dvh`, which would drop the whole
 * declaration where unsupported (see PanelModal's maxHeight comment).
 */
const SIGNIN_CARD_WIDTH = 'min(1500px, 92vw, max(680px, calc((88vh - 220px) * 1.6 + 48px)))';
/**
 * 88vh leaves a clear band of page above and below — a window, not a takeover
 * (owner's choice, 2026-09-04). The 1100px term stops a tall portrait monitor
 * producing a 1900px card whose stage is width-bound anyway.
 */
const SIGNIN_CARD_HEIGHT = 'min(1100px, 88vh)';

/**
 * When the window is too small to carry full chrome AND a usable live view.
 *
 * Gated on the WINDOW and nothing else — gating on the measured slot would be
 * a feedback loop (compact changes the chrome, the chrome changes the slot,
 * the slot changes compact).
 *
 * The height term keeps every laptop in full chrome: 1366x768 gives 768, and
 * the /desktop gate already records that height-only thresholds in the 700s
 * put laptops into a phone layout. The WIDTH term is the one the geometry
 * gate forced: on a 320x568 phone the window is 288px wide, the footer wraps
 * into four rows, and the live view is left with no height at all — measured,
 * not guessed. 380 catches 320 and 360 and leaves 390 (the common iPhone
 * width) in full chrome, where it still fits.
 */
export const COMPACT_MAX_HEIGHT = 560;
export const COMPACT_MAX_WIDTH = 380;
/** Leave compact only this far past the entry line — see the resize handler. */
export const COMPACT_HYSTERESIS_PX = 40;

/**
 * ROOMY chrome — the laptop tier (owner decision 2026-09-04, on the Fable
 * review's measured numbers).
 *
 * Below this window height the 88vh "window, not a takeover" margin costs
 * more than it is worth: at 1366x768, the commonest laptop, chrome was 36%
 * of the card and the live view 698x436 (scale 0.55). Measured alternatives:
 * 96vh alone → 795x497 (0.62); 96vh with the subtitle and the status-row
 * label gone → 827x517 (0.65). So under 820px the card takes 96vh and sheds
 * the two lines that only explain. Everything a user can DO stays. 820 keeps
 * 1440x900 (900) on the default tier and puts 1366x768 and 1280x800 here.
 */
export const ROOMY_MAX_HEIGHT = 820;
/** 2vh of page above and below — the owner accepted the trade for laptops. */
const SIGNIN_CARD_HEIGHT_ROOMY = 'min(1100px, 96vh)';
/** Same shape as SIGNIN_CARD_WIDTH with the roomy height and a chrome
 *  estimate ~30px smaller (no subtitle, no status label). Cosmetic term —
 *  the stage is measured, this only decides the matting. */
const SIGNIN_CARD_WIDTH_ROOMY = 'min(1500px, 92vw, max(680px, calc((96vh - 190px) * 1.6 + 48px)))';

/**
 * PHONE posture (owner decision 2026-09-04, Fable review item 6).
 *
 * Under ~700px of width on a coarse-pointer device the in-page live view is
 * a preview at best — measured 307x192 portrait, 173x108 landscape — and no
 * enlargement is possible in-page (iOS has no fullscreen API for this). The
 * own window IS the phone path: /agent-activity fills the screen and has its
 * own one-tap-landscape affordance. So on a phone the own-window action is
 * PRIMARY — a prominent strip above the live view — rather than a grey
 * button under it. Tablets (coarse, but ≥ 700 wide) stay in-page, where the
 * measured view is usable.
 */
export const PHONE_MAX_WIDTH = 700;

// copy: desktop_signin_pilot_chrome
const SIGNIN_COPY = {
  fullscreen: { en: 'full screen', es: 'pantalla completa' },
  exitFullscreen: { en: 'exit full screen', es: 'salir de pantalla completa' },
  /** Shown in compact chrome, where the window is genuinely short. */
  moreRoom: { en: 'more room in full screen', es: 'más espacio en pantalla completa' },
  /** PHONE posture: the own window is the primary action — see PHONE_MAX_WIDTH. */
  phoneOwnWindow: {
    en: 'on a phone, signing in works best in its own window',
    es: 'en un teléfono, iniciar sesión funciona mejor en su propia ventana',
  },
  /** THE PASTE BRIDGE. A one-time code lives in the user's email, on their
   *  OWN computer — the window here is a browser on another machine, so
   *  ctrl+V inside it pastes that machine's clipboard, which is empty. This
   *  hands the local clipboard across. */
  paste: { en: 'paste from your computer', es: 'pegar desde tu computadora' },
  /** A suggestion for the identity field, never a prefill — see its use. */
  useMyEmail: {
    en: (email: string) => `use ${email}`,
    es: (email: string) => `usar ${email}`,
  },
  pasteBusy: { en: 'sending…', es: 'enviando…' },
  pasteReady: {
    en: 'ready \u2014 now press \u2318V / ctrl+V in the window',
    es: 'listo \u2014 ahora presiona \u2318V / ctrl+V en la ventana',
  },
  pasteEmpty: {
    en: 'nothing copied yet \u2014 copy the code or password first',
    es: 'a\u00fan no has copiado nada \u2014 copia el c\u00f3digo o la contrase\u00f1a primero',
  },
  pasteDenied: {
    en: 'your browser would not share the clipboard \u2014 type it in the window instead',
    es: 'tu navegador no comparti\u00f3 el portapapeles \u2014 escr\u00edbelo en la ventana',
  },
  pasteFailed: {
    en: 'could not reach the window just now \u2014 try again, or type it in',
    es: 'no se pudo llegar a la ventana \u2014 int\u00e9ntalo otra vez o escr\u00edbelo',
  },
} as const;

/** The browser's language, narrowed to the two the copy exists in. */
function browserLang(): 'en' | 'es' {
  try {
    return String(navigator.language || '').toLowerCase().startsWith('es') ? 'es' : 'en';
  } catch {
    return 'en';
  }
}

type BootPhase = 'starting' | 'waking' | 'connecting' | 'live' | 'unreachable' | 'blocked';

type BlockedState = {
  outcome: 'no-workspace' | 'stalled' | 'billing-paused' | 'unreachable';
  reason?: string;
};

const SESSION_START_RETRY_DELAYS_MS = [1500, 3500];
const LIVE_WATCHDOG_MS = 20000;
const KEEPALIVE_INTERVAL_MS = 4 * 60 * 1000; // server idle-reaps at 20 min
const VERIFY_POLL_MS = 5000;

/** Bounded re-drives (2026-08-22 Fitbit incident): Prest0n takes the wheel
 *  back at most twice per desktop session. The gone-page COPY keys on the
 *  same budget — the surface may only promise it is "finding the right one"
 *  while a further drive is genuinely still possible. */
const PILOT_MAX_REDRIVES = 2;

/** Owner directive 2026-08-20: desktop sign-in pop-ups open IN FRONT of all
 *  screens, everywhere — this surface previously sat low enough to open
 *  invisibly UNDER the onboarding catalog modal and under the chat pane that
 *  launched it. The rung, and its place above every other overlay, is
 *  defined with the rest of the ladder in src/styles/layers.ts; this file
 *  used to restate the neighbouring numbers and had three of the four wrong
 *  (2026-09-11). Pinned by desktopSignInModal.test.ts. */
export { DESKTOP_SIGNIN_Z_INDEX } from '../../../styles/layers';

export const DesktopSignInModal: React.FC<DesktopSignInModalProps> = ({
  appName,
  customSlug,
  scope = 'personal',
  sessionId: givenSessionId,
  signInUrl,
  workspaceAccountId,
  lang,
  origin,
  onConnected,
  onClose,
  retryDelaysMs = SESSION_START_RETRY_DELAYS_MS,
  liveWatchdogMs = LIVE_WATCHDOG_MS,
  keepaliveIntervalMs = KEEPALIVE_INTERVAL_MS,
  verifyPollMs = VERIFY_POLL_MS,
  probeEmbedReachable = defaultProbeEmbedReachable,
}) => {
  const [phase, setPhase] = useState<BootPhase>('starting');
  const [session, setSession] = useState<DesktopSignInSession | null>(null);
  const [bootProgress, setBootProgress] = useState<WorkspaceBootProgress | null>(null);
  const [blocked, setBlocked] = useState<BlockedState | null>(null);
  const [minting, setMinting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);
  const [bootAttempt, setBootAttempt] = useState(0);

  // Click→pixels clock (2026-08-31 instant-connect): the 'surface_live'
  // funnel timing every pre-warm graduation measures against (ES-17).
  const bootStartRef = useRef<number>(Date.now());

  // ── Boot: wake gate + session start with backoff (or adopt a handoff). ──
  useEffect(() => {
    let cancelled = false;
    bootStartRef.current = Date.now();
    setPhase('starting');
    setSession(null);
    setBootProgress(null);
    setBlocked(null);
    const adopt = (s: DesktopSignInSession) => {
      if (cancelled) return;
      setSession(s);
      // POST success proves the session is alive. With an embeddable view we
      // wait for its load (watchdog-backed); without one (old service build)
      // the own-window button IS the surface — live immediately.
      setPhase(buildDesktopNoVncUrl(s.base, s.wsPort) ? 'connecting' : 'live');
    };
    const attempt = (i: number): void => {
      startSignInDesktopSession(appName, {
        ...(signInUrl ? { signInUrl } : {}),
        ...(workspaceAccountId ? { workspaceAccountId } : {}),
        // Wake-aware connect (2026-08-19): a sleeping workstation is woken
        // (a missing one provisioned) with honest progress instead of the
        // old silent fallback onto the wrong machine's browser profile.
        onBootProgress: (p) => {
          if (cancelled) return;
          setBootProgress(p);
          setPhase('waking');
        },
      })
        .then(adopt)
        .catch((err) => {
          if (cancelled) return;
          if (err instanceof WorkspaceNotReadyError) {
            // Terminal gate outcomes get their own honest card — retrying
            // the same 6-minute gate on a backoff timer helps nobody.
            setBlocked({ outcome: err.outcome, reason: err.reason });
            setPhase('blocked');
            return;
          }
          if (i < retryDelaysMs.length) {
            window.setTimeout(() => { if (!cancelled) attempt(i + 1); }, retryDelaysMs[i]);
          } else {
            setPhase('unreachable');
          }
        });
    };
    if (givenSessionId) {
      // Chat handoff: the session exists — fetch its wsPort from its own base.
      const base = getRegisteredBrowserSessionBase(givenSessionId);
      void (async () => {
        const info = base ? await getDesktopSessionInfo(givenSessionId, base) : null;
        if (cancelled) return;
        if (info) {
          // The handed-off session predates this modal — adopt IMMEDIATELY
          // with the synchronous ladder (explicit URL / known registry);
          // research must never delay a live session, and a handoff came
          // from the card lane whose research already fed the registry.
          const dest = resolveKnownSignInDestinationSync(appName, signInUrl);
          adopt({
            sessionId: givenSessionId, base: base!, wsPort: info.wsPort,
            resolvedSignInUrl: dest.url, signInUrlSource: dest.source,
          });
        } else attempt(0); // stale handoff — start fresh, same reuse guard applies
      })();
    } else {
      attempt(0);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appName, givenSessionId, signInUrl, workspaceAccountId, bootAttempt]);

  // ── Live gate: the interactive iframe reports load; a watchdog verifies
  // the session against the service instead of leaving a spinner forever. ──
  useEffect(() => {
    if (phase !== 'connecting' || !session) return;
    let cancelled = false;
    const watchdog = window.setTimeout(() => {
      void (async () => {
        const info = await getDesktopSessionInfo(session.sessionId, session.base);
        if (cancelled) return;
        if (info) {
          // Session is alive but the iframe never reported. Two very
          // different causes share that symptom: some browsers swallow
          // cross-origin onload (view actually fine), and a dead noVNC
          // route/TLS failure (a permanently BLANK white iframe — the
          // owner-reported "the desktop never loads", 2026-08-20, which the
          // old code declared 'live' with no error and no retry). An opaque
          // no-cors probe splits them: it resolves when the embed's server
          // answers at all, and rejects on the blank-iframe failure classes
          // (route down, TLS, DNS) — those route to the honest unreachable
          // card, whose retry works.
          const probeUrl = buildDesktopNoVncUrl(session.base, session.wsPort);
          const embedReachable = probeUrl ? await probeEmbedReachable(probeUrl) : true;
          if (cancelled) return;
          if (embedReachable) {
            setPhase('live');
          } else {
            logConnectFunnel({ event: 'no_first_frame', detail: `${session.sessionId} novnc-unreachable` });
            setPhase('unreachable');
          }
        } else {
          logConnectFunnel({ event: 'no_first_frame', detail: session.sessionId });
          setPhase('unreachable');
        }
      })();
    }, liveWatchdogMs);
    return () => {
      cancelled = true;
      window.clearTimeout(watchdog);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, session?.sessionId]);

  // The moment interactive pixels are declared: the ES-17 measurement.
  useEffect(() => {
    if (phase !== 'live') return;
    logConnectFunnel({ event: 'surface_live', ms: Date.now() - bootStartRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Keepalive: the server idle-reaps desktops; ping while the surface is
  // open so a slow sign-in is never reaped mid-flow. ───────────────────────
  useEffect(() => {
    if (!session) return;
    const timer = window.setInterval(() => {
      void sendDesktopKeepalive(session.sessionId, session.base);
    }, keepaliveIntervalMs);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId]);

  const restart = useCallback(() => {
    setSession(null);
    setBootAttempt((n) => n + 1);
  }, []);

  // 'no-workspace' auto-heal (2026-08-27 onboarding dead-end RCA): during a
  // fresh signup the gate can answer no-workspace in the short window before
  // the org back-pointer/mint settles — and the machine is often ALREADY
  // running (the live repro's warm-pool VM sat ready 5m40s before the click;
  // the dead-end would have self-resolved 86s later). A terminal card there
  // strands the user inside their own setup. So no-workspace re-runs the
  // gate on a timer for ~a minute before going terminal; every other
  // terminal outcome (billing-paused especially) keeps its original
  // one-shot honesty.
  const NO_WORKSPACE_AUTO_RETRIES = 8;
  const noWorkspaceRetryDelayMs = 6000;
  const [noWorkspaceRetries, setNoWorkspaceRetries] = useState(0);
  useEffect(() => { setNoWorkspaceRetries(0); }, [appName, workspaceAccountId]);
  useEffect(() => {
    if (phase !== 'blocked' || blocked?.outcome !== 'no-workspace') return;
    if (noWorkspaceRetries >= NO_WORKSPACE_AUTO_RETRIES) return;
    const timer = window.setTimeout(() => {
      setNoWorkspaceRetries((n) => n + 1);
      restart();
    }, noWorkspaceRetryDelayMs);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, blocked, noWorkspaceRetries]);

  // ── prest0n DRIVES the browser to the sign-in page (2026-08-18): the
  // computer-use pilot on the session's VM navigates/clicks its way to the
  // exact sign-in location while the user watches — it cannot type by
  // construction, so credentials stay the human's job. A STUCK pilot says so
  // (owner directive 2026-08-20: never a silent give-up — the user was left
  // staring at the wrong page with no hint to navigate manually); an
  // unavailable pilot (old VM build) stays quiet — the loaded sign-in URL
  // is already the right page there, nothing was attempted or lost. ───────
  // THE destination for this journey (2026-08-20 Kalshi repro): resolved by
  // the destination ladder at session start — every consumer below (pilot,
  // page-state scope, retarget belt, guidance, promotion probe target) keys
  // on it, never on the maybe-absent prop alone.
  //
  // ONCE A SESSION EXISTS, ITS RESOLVED URL IS THE ONLY TRUTH (2026-09-02,
  // review finding on contract C8). The ladder refuses a search engine on
  // every rung and the deterministic navigations below trust that. With the
  // fabricated rung retired the ladder can now answer '', and an
  // `|| signInUrl` fallback would have re-admitted the raw prop on exactly
  // that path — including the search-engine URL the ladder had just refused
  // (the prop reaches this surface through callers that filter for `https:`
  // and nothing else: the card's data-sign-in-url, beginCustomConnectFlow,
  // DesktopSignInModalHost). Before a session exists the prop is run through
  // the same synchronous ladder, so no code path here can ever hold a
  // destination resolveSignInDestination would have refused.
  const propDest = resolveKnownSignInDestinationSync(appName, signInUrl);
  const destUrl = session ? session.resolvedSignInUrl : propDest.url;
  const destSource: DesktopSignInSession['signInUrlSource'] =
    session ? session.signInUrlSource : propDest.source;

  const [pilot, setPilot] = useState<'idle' | 'driving' | 'arrived' | 'manual' | 'notfound' | 'nodestination' | 'off'>('idle');
  // The verdict the self-heal report is built from (contract C4). Held in
  // state rather than reported inline: the journey the report cites may open
  // a beat after the drive ends, and a report without it is refused server-
  // side for a malformed id.
  const [pilotVerdict, setPilotVerdict] = useState<
    { outcome: SignInPilotOutcome; landing?: SignInPilotLanding } | null
  >(null);
  // Re-drive counter (2026-08-22 Fitbit incident): the pilot used to be
  // one-shot per session — after a redirect parked the browser off-app,
  // nothing ever re-took the wheel. The poll below bumps this to
  // re-dispatch the pilot (bounded) when the page stays off the app.
  const [pilotRun, setPilotRun] = useState(0);
  // Declared HERE (not beside the poll below) because the pilot effect's copy
  // decision reads the same budget: what the surface promises and what it can
  // actually do must come from one number.
  const pilotRedrivesRef = useRef(0);
  useEffect(() => {
    if (!session) return;
    // Nowhere to drive: say so instead of driving at nothing and reporting
    // that the drive failed (2026-09-02 review). The pilot is not started —
    // there is no address for it — so no verdict is minted either.
    if (!destUrl) {
      setPilot('nodestination');
      setPilotVerdict(null);
      return;
    }
    let cancelled = false;
    setPilot('driving');
    // A new drive invalidates the previous verdict NOW — a stale landing must
    // never be reported against the journey of a session it did not happen in.
    setPilotVerdict(null);
    // signInUrl (2026-08-20): the pilot navigates straight to the researched
    // official page (never a search engine) and reconciles its verdict against
    // the live URL, so "couldn't reach the sign-in page" can no longer show
    // over a page it actually reached.
    void runSignInPilotTask(session.sessionId, appName, session.base, destUrl).then((verdict) => {
      if (cancelled) return;
      const { outcome, landing } = verdict;
      // A landing the workstation classified as GONE gets its own honest
      // line (the destination is wrong, not the drive) — but ONLY while the
      // search it promises is still running. "finding the right one" is a
      // promise kept by the bounded re-drive below; once that budget is
      // spent, or there is no destination to re-drive to, the surface must
      // hand the user the ACTIONABLE line instead of claiming a search that
      // has stopped (2026-09-02 review finding — the same "sign in here over
      // a store page, forever" class the blind floor was fixed for).
      // wrong-destination (the Fitbit store-redirect class) keeps the manual
      // line: that page is not gone, it is the wrong one, and the honest
      // thing to say is how to get to the right one.
      const gone = landedOnAGonePage(landing);
      const stillLooking = !!destUrl && pilotRedrivesRef.current < PILOT_MAX_REDRIVES;
      setPilot(
        outcome === 'login-form' ? 'arrived'
          : gone && stillLooking ? 'notfound'
          : gone || outcome === 'stuck' || outcome === 'wrong-destination' ? 'manual'
          : 'off'
      );
      setPilotVerdict({ outcome, ...(landing ? { landing } : {}) });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId, appName, destUrl, pilotRun]);

  // ── Co-Drive journey (Phase 0 — docs/FRAMEWORK-computer-use-co-drive.md
  // §6): open/adopt the journey on the VM once per session and subscribe to
  // its Firestore doc. The ONLY journey-driven chrome in Phase 0 is the
  // persistent pause control — every other render keeps its local
  // derivation, so an old VM build (404 on the journey route) or the
  // platform-shared host (accountId-mismatch degrade) leaves the surface
  // exactly as it was before journeys existed. ──
  const [journeyRef, setJourneyRef] = useState<{ journeyId: string; accountId: string } | null>(null);
  const [journeyDoc, setJourneyDoc] = useState<JourneyDocSnapshot | null>(null);
  const [journeyControlPending, setJourneyControlPending] = useState(false);
  const journeySessionRef = useRef<string | null>(null);
  // The journey we actually opened (session + base), for the close-on-unmount
  // ceremony — a journey must never outlive the surface that drives it.
  const openedJourneyRef = useRef<{ sessionId: string; base: string } | null>(null);
  useEffect(() => {
    if (!session || journeySessionRef.current === session.sessionId) return;
    journeySessionRef.current = session.sessionId;
    // A re-minted session invalidates the previous journey NOW — a stale
    // pill must never render from a dead journey's doc while its controls
    // post to the new session (2026-08-21 review finding).
    const prior = openedJourneyRef.current;
    if (prior && prior.sessionId !== session.sessionId) {
      openedJourneyRef.current = null;
      void postJourneyControl(prior.sessionId, prior.base, 'close');
    }
    setJourneyRef(null);
    let cancelled = false;
    void (async () => {
      try {
        const uid = getAuth().currentUser?.uid;
        if (!uid) return;
        const resolution = await resolveAccount(uid).catch(() => null);
        const accountId = resolution?.accountId ?? uid;
        const opened = await openSignInJourney(session.sessionId, session.base, {
          appName,
          accountId,
          slug: customSlug ?? null,
          signInUrl: destUrl ?? null,
          source: destSource,
          userId: uid,
        });
        // Belt + suspenders with the VM's own check: subscribe only when the
        // journey's account is the client-resolved org (the runtime-id trap).
        if (!cancelled && opened && opened.accountId === accountId) {
          openedJourneyRef.current = { sessionId: session.sessionId, base: session.base };
          setJourneyRef(opened);
        }
      } catch { /* no journey chrome — today's surface, unchanged */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId]);
  useEffect(() => {
    if (!journeyRef) { setJourneyDoc(null); return; }
    return subscribeToJourney(journeyRef.accountId, journeyRef.journeyId, setJourneyDoc);
  }, [journeyRef]);
  // Close-on-unmount: the surface going away ends its journey (fire-and-
  // forget; a finished journey no-ops server-side). Without this, closing
  // the modal mid-sign-in leaked a live journey whose interlock and blackout
  // outlived the surface (2026-08-21 review, the critical finding).
  useEffect(() => () => {
    const opened = openedJourneyRef.current;
    if (opened) void postJourneyControl(opened.sessionId, opened.base, 'close');
  }, []);
  const journeyPaused = journeyDoc?.control === 'paused_by_user';
  const journeyLive =
    !!journeyDoc?.control && !isTerminalControl(journeyDoc.control as ControlState);
  const toggleJourneyPause = useCallback(async () => {
    if (!session || !journeyRef || journeyControlPending) return;
    setJourneyControlPending(true);
    try {
      await postJourneyControl(session.sessionId, session.base, journeyPaused ? 'resume' : 'pause');
    } catch { /* the doc snapshot is the truth; the button stays tappable */ }
    setJourneyControlPending(false);
  }, [session, journeyRef, journeyControlPending, journeyPaused]);

  // ── Destination self-heal (contract C3, 2026-09-02) ──────────────────
  // A destination that landed off the app, or on a page that is gone, is
  // REPORTED once per desktop session. The body is a hint, never proof: the
  // server counts a strike only from the workstation's own journey receipts
  // (it reads them itself), so this can never be the thing that retires a
  // working destination — and until the VM writes landing receipts it is
  // log-only by construction. Fire-and-forget: the helper never throws, an
  // older server answering 404 'Unknown action' reads as "not applied", and
  // nothing here ever blocks or interrupts the sign-in.
  const reportedSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!session || !destUrl || !journeyRef || !pilotVerdict) return;
    if (reportedSessionRef.current === session.sessionId) return;
    const { outcome, landing } = pilotVerdict;
    if (!destinationLandedWrong(outcome, landing)) return;
    reportedSessionRef.current = session.sessionId;
    // Contract C7: the funnel union carries 'destination_reported', so the
    // report is measurable from the day it ships — how often a stored
    // destination lands wrong is the whole point of the self-heal.
    logConnectFunnel({
      event: 'destination_reported',
      detail: `${appName}: ${landing?.kind || outcome}`,
    });
    void reportDestination({
      app: appName,
      url: destUrl,
      landedUrl: landing?.url || '',
      ...(landing?.kind ? { landedKind: landing.kind } : {}),
      ...(typeof landing?.status === 'number' ? { landedStatus: landing.status } : {}),
      accountId: journeyRef.accountId,
      journeyId: journeyRef.journeyId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId, destUrl, journeyRef, pilotVerdict, appName]);

  // Mint AT CLICK TIME — never a pre-rendered (aging) link.
  const openLiveDesktop = useCallback(async () => {
    if (!session || minting) return;
    setMinting(true);
    try {
      const link = await mintDeepLinkUrl(session.sessionId);
      if (link.url) {
        // Sessions living on the ACCOUNT VM carry their service base into
        // the deep-link page (validated svc override there) — without it
        // the page would look for the session on the shared host and find
        // nothing.
        const vmBase = getRegisteredBrowserSessionBase(session.sessionId);
        const url = vmBase
          ? `${link.url}${link.url.includes('?') ? '&' : '?'}svc=${encodeURIComponent(vmBase)}`
          : link.url;
        logConnectFunnel({ event: 'deep_link_minted', detail: session.sessionId });
        window.open(url, 'prest0n-desktop', 'width=1200,height=800');
      }
    } catch { /* the button stays; the user can click again */ }
    setMinting(false);
  }, [session, minting]);

  // Access controls, one tap, right in the connect popup (2026-08-18 owner
  // feedback): the same category presets every catalog app gets. Everything
  // is pre-approved (the founder default) — the chips are the one-click
  // EDIT; leaving them untouched IS the approval.
  //
  // Saved by a SLUG-KEYED edit (P0 #6 "access truth", 2026-09-02). It used
  // to re-POST the whole registration, and the register path recomputes
  // scope from the body — with `personalOnly` absent that wrote the scope
  // workspace's collection, growing an organization husk beside a
  // personal-only connection. The edit now names only the slug and the
  // access; where the doc lives is the server's existing fact, never
  // re-decided here. An older server has no such action and answers 400
  // (the register fallthrough needs a name) — the chip reverts, and nothing
  // is ever re-registered behind the user's back.
  const [customAccess, setCustomAccess] = useState<'full' | 'read-write' | 'read-only'>('full');
  // Is `customAccess` the STORED value, or still this component's default?
  // The surface now also opens on a connection that already stands (the
  // "sign in to <app> again" card), where the stored access may be
  // read-only — showing "✓ everything (default)" there would be a lie, and
  // tapping a chip would write the lie back. Until the roster answers, the
  // chips render with NO active mark: a tap is then an explicit choice, not
  // a confirmation of something the surface guessed (2026-09-02 review).
  const [accessKnown, setAccessKnown] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  // The chips stay tappable while the roster read is in flight, so a tap can
  // land first and its save can even complete first. The roster answer is
  // then STALE — applying it would undo the user's edit on screen while the
  // stored doc says otherwise (2026-09-02 review). A local edit wins for the
  // life of this surface.
  const accessEditedRef = useRef(false);
  useEffect(() => {
    if (!customSlug) { setAccessKnown(false); return; }
    let cancelled = false;
    void listCustomConnections()
      .then((rows) => {
        if (cancelled || accessEditedRef.current) return;
        const row = rows.find((c) => String(c.slug || '') === customSlug);
        if (!row) return; // not on this plane (a personalOnly doc) — stay unmarked
        const stored = row.access;
        if (stored === 'full' || stored === 'read-write' || stored === 'read-only') {
          setCustomAccess(stored);
          setAccessKnown(true);
        }
      })
      .catch(() => { /* roster unknown — the chips stay unmarked */ });
    return () => { cancelled = true; };
  }, [customSlug]);
  const pickCustomAccess = useCallback(async (access: 'full' | 'read-write' | 'read-only') => {
    if (!customSlug || accessSaving) return;
    const prev = customAccess;
    const prevKnown = accessKnown;
    accessEditedRef.current = true;
    setCustomAccess(access);
    setAccessKnown(true);
    setAccessSaving(true);
    try {
      // NO name, NO scope (2026-09-02): this is an ACCESS edit on a doc that
      // already exists. The register path recomputes scope from the body and
      // picks the collection from `personalOnly` — that is how a
      // personal-only connection grew an organization husk, and how sending
      // this component's default scope silently converted an
      // organization-scoped connection to personal. The slug-keyed edit
      // touches one field on the doc where it already lives.
      const res = await updateCustomConnectionAccess(customSlug, access);
      // Shape check, not just a 2xx: only { ok, slug, access } is an applied
      // edit. Anything else means an older server answered something we did
      // not ask for.
      if (!res || res.ok !== true || res.slug !== customSlug || res.access !== access) {
        setCustomAccess(prev);
        setAccessKnown(prevKnown);
        // Not applied is not a save: the stored value is still authoritative,
        // so a roster answer may seed the chips again (same reasoning as the
        // catch below — a refused edit must not latch the surface).
        accessEditedRef.current = false;
      }
    } catch {
      setCustomAccess(prev); // revert quietly; the row stays tappable
      setAccessKnown(prevKnown);
      // The save did not land, so the stored value is still authoritative —
      // let a roster answer seed the chips again.
      accessEditedRef.current = false;
    }
    setAccessSaving(false);
  }, [customSlug, accessSaving, accessKnown, customAccess]);

  // The conversation's language, resolved ONCE (2026-09-02): the chat lane
  // knows it; the browser's own locale is the wrong answer for a Spanish
  // speaker on an English machine, and is only the fallback.
  const uiLang: 'en' | 'es' = lang ?? browserLang();
  // Bigger still (2026-09-03 owner directive). Default is already as large as
  // the viewport sensibly allows; this fills the window for a page that needs
  // every pixel — a dense enterprise SSO form, a small one-time-code field.
  const [maximised, setMaximised] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { slotRef, fit, remeasure } = useStageFit();
  // CHROME TIERS — see COMPACT_*, ROOMY_MAX_HEIGHT and PHONE_MAX_WIDTH above.
  // `compact` strips what only explains; `roomy` adds height and drops the
  // subtitle; `phone` promotes the own-window action. Gated on the WINDOW
  // only — gating on the measured slot would be a feedback loop.
  const coarsePointer = () => {
    try {
      return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
    } catch {
      return false;
    }
  };
  const [compact, setCompact] = useState(
    () => (typeof window !== 'undefined'
      ? window.innerHeight < COMPACT_MAX_HEIGHT || window.innerWidth < COMPACT_MAX_WIDTH
      : false),
  );
  const [roomy, setRoomy] = useState(
    () => (typeof window !== 'undefined' ? window.innerHeight < ROOMY_MAX_HEIGHT : false),
  );
  const [phone, setPhone] = useState(
    () => (typeof window !== 'undefined'
      ? coarsePointer() && (window.innerWidth < PHONE_MAX_WIDTH || window.innerHeight < COMPACT_MAX_HEIGHT)
      : false),
  );
  useEffect(() => {
    // HYSTERESIS (Fable review of #1586): engines that shrink the layout
    // viewport for the on-screen keyboard (Firefox Android, older Chrome)
    // fire `resize` when the user taps the identity field. Without a gap
    // between the enter and leave thresholds, a device sitting near the
    // line would flip compact on focus, unmount the field under the finger,
    // close the keyboard, grow again, remount, and loop. Roomy gets the same
    // gap for the same reason; phone posture is a property of the device
    // plus a width, which the keyboard does not change.
    const onResize = () => {
      const h = window.innerHeight;
      const w = window.innerWidth;
      setCompact((prev) => {
        const enter = h < COMPACT_MAX_HEIGHT || w < COMPACT_MAX_WIDTH;
        const leave = h >= COMPACT_MAX_HEIGHT + COMPACT_HYSTERESIS_PX
          && w >= COMPACT_MAX_WIDTH + COMPACT_HYSTERESIS_PX;
        if (prev) return !leave;
        return enter;
      });
      setRoomy((prev) => {
        if (prev) return h < ROOMY_MAX_HEIGHT + COMPACT_HYSTERESIS_PX;
        return h < ROOMY_MAX_HEIGHT;
      });
      setPhone(coarsePointer() && (w < PHONE_MAX_WIDTH || h < COMPACT_MAX_HEIGHT));
    };
    onResize();
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const fullscreenSupported = (() => {
    try {
      return !!(
        document.fullscreenEnabled
        || (document as unknown as { webkitFullscreenEnabled?: boolean }).webkitFullscreenEnabled
      );
    } catch {
      return false;
    }
  })();

  // THE CARD goes fullscreen, not the document (which would keep the card's
  // own margins and gain nothing) and not the stage alone (the done button,
  // the workspace toggle and the identity field would all be unreachable
  // while the user is still finishing). A fullscreen element sits in the top
  // layer, so z-index stops mattering entirely while it is up.
  const enterFullscreen = useCallback(async () => {
    const el = cardRef.current;
    if (!el) return;
    try {
      if (el.requestFullscreen) await el.requestFullscreen({ navigationUI: 'hide' });
      else if ((el as unknown as { webkitRequestFullscreen?: () => void }).webkitRequestFullscreen) {
        (el as unknown as { webkitRequestFullscreen: () => void }).webkitRequestFullscreen();
      } else {
        // Unreachable when fullscreenSupported gates the button, kept as the
        // belt: cover the viewport with CSS rather than do nothing.
        setMaximised(true);
      }
    } catch {
      // The API exists but REFUSED (a permissions policy, a nested frame).
      // Cover the viewport with CSS: PanelModal's `flush` drops the overlay
      // padding so this is a true cover, not a card in a 16px gutter (Fable
      // review of #1586 — that gutter was also the backdrop-close target).
      setMaximised(true);
    }
    try {
      // Coarse pointers only — locking a laptop's orientation means nothing
      // and some engines reject it outright.
      // `window.screen`, not bare `screen` — no-restricted-globals, which
      // CRA reports as a warning locally and an ERROR under CI.
      const orientation = (window.screen as unknown as {
        orientation?: { lock?: (o: string) => Promise<void> };
      }).orientation;
      if (window.matchMedia('(pointer:coarse)').matches && orientation?.lock) {
        await orientation.lock('landscape');
      }
    } catch {
      /* orientation lock unavailable or refused */
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    try {
      (window.screen as unknown as { orientation?: { unlock?: () => void } }).orientation?.unlock?.();
    } catch {
      /* nothing locked */
    }
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if ((document as unknown as { webkitExitFullscreen?: () => void }).webkitExitFullscreen) {
        (document as unknown as { webkitExitFullscreen: () => void }).webkitExitFullscreen();
      }
    } catch {
      /* already out */
    }
    setMaximised(false);
  }, []);

  // STATE COMES FROM THE EVENT, NEVER FROM THE CLICK: F11, Escape and the
  // browser's own chrome all leave fullscreen without telling us, and a
  // button stuck on "exit full screen" over a windowed modal is a small lie.
  useEffect(() => {
    const sync = () => {
      let el: Element | null = null;
      try {
        el = (document.fullscreenElement
          || (document as unknown as { webkitFullscreenElement?: Element | null })
            .webkitFullscreenElement
          || null) as Element | null;
      } catch {
        el = null;
      }
      const card = cardRef.current;
      const nowMaximised = !!el && !!card && (el === card || el.contains(card));
      setMaximised(nowMaximised);
      if (!nowMaximised) {
        // Escape, the back gesture and the browser's own chrome all leave
        // fullscreen without passing through exitFullscreen — release the
        // orientation lock here too, or a phone stays pinned to landscape.
        try {
          (window.screen as unknown as { orientation?: { unlock?: () => void } })
            .orientation?.unlock?.();
        } catch {
          /* nothing locked */
        }
      }
      window.setTimeout(remeasure, 350);
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync as EventListener);
    };
  }, [remeasure]);
  // The address this person signed into Prest0n with — offered as the
  // identity for this connection, one tap, never assumed.
  const accountEmail = (() => {
    try {
      return String(getAuth().currentUser?.email || '').trim().slice(0, 120);
    } catch {
      return '';
    }
  })();
  // Clipboard bridge state — see pasteFromLocalClipboard below.
  const [pasteState, setPasteState] = useState<'idle' | 'busy' | 'ready' | 'empty' | 'denied' | 'failed'>('idle');
  // WHICH account (2026-08-18 owner feedback): every login carries an
  // identity summary. Custom systems have no provider callback to read it
  // from, so the user may note it here — optional, never a gate.
  const [signedInAs, setSignedInAs] = useState('');
  // W-R3 (owner ruling 2026-08-25): the share choice is uniform on every
  // connect surface. When this sign-in runs in an ORGANIZATION workspace
  // context (explicit onboarding target, or the active workspace is an
  // org), the completion row carries "use on this workspace" — ON by
  // default; OFF finishes the connection onto the user's own
  // account-attached plane ("my connections") and cleans up the org's
  // registration husk server-side.
  const [workspaceShareTarget, setWorkspaceShareTarget] = useState<{ accountId: string; label: string } | null>(null);
  const [useOnWorkspace, setUseOnWorkspace] = useState(true);
  useEffect(() => {
    let alive = true;
    const uid = getAuth().currentUser?.uid ?? null;
    void fetchWorkspaceOptionsCached(uid).then((options) => {
      if (!alive) return;
      const target = workspaceAccountId
        ? options.find((o) => o.accountId === workspaceAccountId)
        : options.find((o) => o.isActive);
      const targetId = workspaceAccountId || target?.accountId || null;
      if (targetId && uid && targetId !== uid) {
        setWorkspaceShareTarget({ accountId: targetId, label: workspaceLabel({ accountId: targetId, displayName: target?.displayName || '' } as any) });
      }
    }).catch(() => { /* no context → no checkbox; scope default applies */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceAccountId]);


  // ── Live page-state poll (2026-08-19 one-tap confirm + 2026-08-20 truthful
  // guidance): while the surface is live, poll the session's page state,
  // scoped to THIS app so a positive verdict is impossible on a search or
  // bot-challenge page or somebody else's site. Two jobs:
  //   1. Detection-assisted completion — when the page looks signed in TO
  //      THIS APP, pre-highlight the finish button; and when the verdict is
  //      HIGH-confidence and holds for two consecutive ticks, Prest0n
  //      completes the connection itself (2026-08-22 owner directive: "Prest0n
  //      must realize when the user is fully signed in" — supersedes the
  //      2026-08-19 tap-only decision; the tap remains for medium confidence
  //      and as the always-available manual path).
  //   2. Human-step guidance — when the page is a captcha/2FA/credential wall,
  //      show a pop-over OVER the live view composed by the computer-use
  //      plane (text/position/affordance), then clear it once the step is
  //      past and prest0n resumes. Old VMs return null → nothing shows. ──
  const [verified, setVerified] = useState(false);
  const verifiedTicksRef = useRef(0);
  const autoFinishFiredRef = useRef(false);
  const finishSignInRef = useRef<() => void>(() => undefined);
  const [guidance, setGuidance] = useState<ActiveGuidance | null>(null);
  const guideKindRef = useRef<DesktopPageKind | null>(null);
  const floorShownRef = useRef(false);
  const offAppTicksRef = useRef(0);
  useEffect(() => {
    if (phase !== 'live' || !session) return;
    let cancelled = false;
    const scope = { appName, signInUrl: destUrl };
    const tick = () => {
      void checkSignInStatus(session.sessionId, session.base, scope).then(async (status) => {
        if (cancelled || !status) return;
        // The blind floor yields the moment REAL page-state arrives — its
        // copy was written for the no-evidence case and must never stand
        // over live evidence (2026-08-22 Fitbit incident: it claimed a
        // sign-in page over a store page, forever).
        if (floorShownRef.current) {
          floorShownRef.current = false;
          guideKindRef.current = null;
          setGuidance(null);
        }
        // Off-app watch: when the page sits on neither the app nor a login
        // surface for ~3 ticks, Prest0n takes the wheel back (bounded).
        // A landing the workstation classified as GONE is off-target on ANY
        // host (2026-09-02, plan row D2): a 404 on the app's own domain used
        // to read as kind 'other' + belongs-to-app, so the wheel was never
        // taken back from a dead page.
        // 'signup' is judged by HOST, exactly like 'search'/'other' — the
        // app's own create-account page is a legitimate stop on a sign-in
        // journey, a signup page on somebody else's host is not. Widening
        // DESKTOP_PAGE_KINDS with 'signup' would otherwise have EXEMPTED it
        // from this watch (it used to coerce to 'other' and be judged here),
        // so a future workstation's signup landing could never be re-driven
        // (2026-09-02 review finding).
        const offApp =
          status.kind === 'notfound' ||
          ((status.kind === 'search' || status.kind === 'other' || status.kind === 'signup') &&
            (!status.url || !destUrl || !pageBelongsToApp(status.url, appName, destUrl)));
        if (offApp) {
          offAppTicksRef.current += 1;
          const paused = journeyDoc?.control === 'paused_by_user';
          if (offAppTicksRef.current >= 3 && pilot !== 'driving' && !paused) {
            if (pilotRedrivesRef.current < PILOT_MAX_REDRIVES && destUrl) {
              pilotRedrivesRef.current += 1;
              offAppTicksRef.current = 0;
              logConnectFunnel({ event: 'pilot_redriven', detail: `${appName} #${pilotRedrivesRef.current}` });
              setPilotRun((r) => r + 1);
            } else if (pilot === 'notfound') {
              // The bounded search is over and the page is STILL off-target:
              // stop promising a search that has stopped and hand the user
              // the line they can act on (2026-09-02 review finding). Belt to
              // the verdict-time check above — this covers a last drive whose
              // verdict never arrives.
              setPilot('manual');
            }
          }
        } else {
          offAppTicksRef.current = 0;
        }
        // Completion assist (custom lane only — the save target).
        if (customSlug && !verified && status.signedIn && status.confidence !== 'low') {
          logConnectFunnel({ event: 'signin_verified', detail: status.confidence });
          setVerified(true);
        }
        // Auto-completion (2026-08-22 owner directive): a HIGH-confidence
        // signed-in verdict sustained for two consecutive ticks means the
        // user finished — Prest0n completes the connection itself instead of
        // waiting for a tap that often never comes (the Oura repro: signed
        // in, surface closed, tile stuck on "sign-in not finished" forever).
        // Bounded and respectful: never while the user has paused Prest0n,
        // never twice, and the manual button stays for medium confidence.
        if (customSlug && status.signedIn && status.confidence === 'high') {
          verifiedTicksRef.current += 1;
          const paused = journeyDoc?.control === 'paused_by_user';
          if (verifiedTicksRef.current >= 2 && !autoFinishFiredRef.current && !paused) {
            autoFinishFiredRef.current = true;
            logConnectFunnel({ event: 'signin_auto_promoted', detail: appName });
            finishSignInRef.current();
          }
        } else {
          verifiedTicksRef.current = 0;
        }
        // Human-only obstacle → guidance pop-over. Fetch fresh copy only when
        // the KIND changes (server caches per url+kind; this avoids a model
        // call every tick). Clear guidance the moment the obstacle is gone.
        const humanStep: DesktopPageKind | null =
          status.kind === 'challenge' || status.kind === '2fa' || status.kind === 'login'
            ? status.kind
            : null;
        if (humanStep) {
          if (guideKindRef.current !== humanStep) {
            guideKindRef.current = humanStep;
            const guide = await fetchOverlayGuide(session.sessionId, session.base, appName, humanStep);
            if (cancelled) return;
            if (guide) setGuidance({ guide, obstacle: status.obstacle, kind: humanStep });
          } else if (status.obstacle) {
            // Same kind, refreshed obstacle position — keep copy, move the anchor.
            setGuidance((prev) => (prev ? { ...prev, obstacle: status.obstacle } : prev));
          }
        } else {
          guideKindRef.current = null;
          setGuidance(null);
        }
      });
    };
    tick();
    const timer = window.setInterval(tick, verifyPollMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, session?.sessionId, customSlug, verified, appName, destUrl, pilot, journeyDoc?.control]);

  // ── Retarget belt + guidance floor (2026-08-20 Kalshi repro; hardened
  // per the 2026-08-21 adversarial review) ─────────────────────────────
  // Fires ONCE PER SESSION, at ADOPTION — before any interactive view has
  // rendered, so it can never clobber something the user typed (the old
  // phase-'live' trigger could fire up to 20s after the surface became
  // interactive via the watchdog path). Decision matrix:
  //   page-state OK      → navigate only on POSITIVE wrong-page evidence;
  //                        login/2fa/challenge/signedin and app-host pages
  //                        are never touched. Floor never shows (the VM's
  //                        composed guidance owns the overlay).
  //   route missing(404) → definitively an old build: fresh sessions get
  //                        the deterministic navigate + the local floor;
  //                        HANDOFF sessions get the floor only — the user
  //                        may already be mid-login in the session's other
  //                        surface, so no navigation without evidence.
  //   transient failure  → one retry; still failing → do NOTHING. A blip
  //                        must not trigger a navigate or the old-VM floor
  //                        on a current VM.
  const beltSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!session || !destUrl || beltSessionRef.current === session.sessionId) return;
    beltSessionRef.current = session.sessionId;
    floorShownRef.current = false;
    offAppTicksRef.current = 0;
    pilotRedrivesRef.current = 0;
    verifiedTicksRef.current = 0;
    autoFinishFiredRef.current = false;
    let cancelled = false;
    const isHandoff = Boolean(givenSessionId);
    void (async () => {
      const scope = { appName, signInUrl: destUrl };
      let probe = await checkSignInStatusDetailed(session.sessionId, session.base, scope);
      if (cancelled) return;
      if (!probe.status && !probe.routeMissing) {
        // Transient — one bounded retry before concluding anything.
        await new Promise((r) => setTimeout(r, 1200));
        if (cancelled) return;
        probe = await checkSignInStatusDetailed(session.sessionId, session.base, scope);
        if (cancelled) return;
      }
      const showFloor = () => {
        floorShownRef.current = true;
        logConnectFunnel({ event: 'guidance_floor_shown', detail: appName });
        setGuidance({
          guide: {
            // ONE honest line (2026-09-02, contract C8): the fabricated
            // destination rung is gone, so every destination that reaches
            // this floor came from a real source and the copy no longer has
            // to hedge about which domain the window is on. It still admits
            // the page may be wrong and keeps the password promise intact.
            text: `sign in to ${appName.toLowerCase()} in the window — if it isn't showing the sign-in page, use the address bar to get there. tick "stay signed in" if it's offered. your details stay in the browser and never touch Prest0n.`,
            xPct: 50,
            // Bottom band — login forms live top/center; the floor guidance
            // must never cover the fields it is pointing the user at.
            yPct: 84,
            affordance: 'none',
          },
          obstacle: null,
          kind: 'login',
        });
      };
      if (probe.status) {
        const st = probe.status;
        const alreadyThere =
          st.kind === 'login' || st.kind === '2fa' || st.kind === 'challenge' || st.kind === 'signedin' ||
          (!!st.url && pageBelongsToApp(st.url, appName, destUrl));
        if (!alreadyThere) {
          const ok = await navigateDesktopSession(session.sessionId, session.base, destUrl);
          if (ok) logConnectFunnel({ event: 'destination_retargeted', detail: destUrl });
        }
        return; // page-state lane is alive — VM guidance owns the overlay.
      }
      if (probe.routeMissing) {
        if (!isHandoff) {
          const ok = await navigateDesktopSession(session.sessionId, session.base, destUrl);
          if (ok) logConnectFunnel({ event: 'destination_retargeted', detail: destUrl });
        }
        if (!cancelled) showFloor();
      }
      // Neither status nor routeMissing after a retry: transient — stand down.
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.sessionId, destUrl, destSource, appName, givenSessionId]);

  /**
   * Hand the user's own clipboard to the window (2026-09-03). Reading it must
   * happen inside this click — a clipboard read outside a user gesture is
   * refused by every browser, and rightly.
   *
   * The note it leaves clears itself: a stale "ready" chip sitting over the
   * page ten minutes later would be a small lie about the state of the
   * clipboard.
   */
  const pasteIntoWindow = useCallback(async () => {
    if (!session) return;
    setPasteState('busy');
    const outcome = await pushLocalClipboardToDesktop(session.sessionId, session.base);
    setPasteState(
      outcome === 'ok' ? 'ready' : outcome === 'empty' ? 'empty' : outcome === 'denied' ? 'denied' : 'failed',
    );
    logConnectFunnel({ event: 'clipboard_pushed', detail: outcome, toolkitSlug: customSlug || '' });
  }, [session, customSlug]);
  useEffect(() => {
    if (pasteState === 'idle' || pasteState === 'busy') return undefined;
    const t = setTimeout(() => setPasteState('idle'), 12000);
    return () => clearTimeout(t);
  }, [pasteState]);

  const finishSignIn = useCallback(async () => {
    if (finishing) return;
    setFinishing(true);
    setFinishError(null);
    try {
      // customSlug is normally minted at registration. If it is missing here
      // the registration silently failed (2026-08-20 truth leak: the modal
      // used to skip completion yet still report success — the browser was
      // signed in but nothing was recorded). Re-register now to get a slug so
      // the promotion actually lands; a failure surfaces instead of a fake ✓.
      const personalOnly = Boolean(workspaceShareTarget) && !useOnWorkspace;
      let slug = customSlug;
      if (!slug) {
        const reg = await registerCustomConnection({ name: appName, scope, access: customAccess, ...(personalOnly ? { personalOnly: true } : {}) });
        slug = reg.slug;
      }
      // Journey verdict (fire-and-forget, NEVER gates the promotion): the VM
      // computes it from a fresh observation and closes the journey `done`.
      if (session && journeyRef) {
        void postJourneyControl(session.sessionId, session.base, 'attest_done');
      }
      // The probe target persists ONLY when the destination was verified-
      // grade (explicit/known/researched). With the fabricated rung retired
      // (contract C8) 'none' is the only unverified source left — and it
      // carries no url to persist anyway.
      const probeTarget = destSource !== 'none' ? destUrl : undefined;
      await completeCustomConnection(slug, signedInAs.trim() || undefined, probeTarget, personalOnly);
      invalidateConnectedAppsCache();
      void import('../../prest0nSession/client')
        .then((m) => m.reconcilePrest0nConnections())
        .catch(() => { /* best-effort */ });
      // ONE announcement, every mount (2026-09-03). `onConnected` only ever
      // reached the code that mounted this component — and the mount the chat
      // lane uses (DesktopSignInModalHost) passed none, so the most common
      // sign-in in the product finished and told nobody. Both fire: the prop
      // for the in-tree caller, the event for everyone else, including the
      // thread waiting on this to carry on with what the user actually asked.
      announceConnectSignedIn({
        appName,
        ...(slug ? { customSlug: slug } : {}),
        ...(signedInAs.trim() ? { signedInAs: signedInAs.trim().slice(0, 200) } : {}),
        ...(origin ? { origin } : {}),
      });
      onConnected?.();
      onClose();
    } catch (err) {
      setFinishError(err instanceof Error ? err.message : 'could not save — try again');
      setFinishing(false);
    }
  }, [customSlug, finishing, signedInAs, destUrl, destSource, appName, scope, customAccess, session, journeyRef, onConnected, onClose, workspaceShareTarget, useOnWorkspace, origin]);
  // Latest-finish ref: the poll effect auto-completes through this without
  // adding finishSignIn (and its whole dep set) to the poll's dependencies.
  finishSignInRef.current = () => { void finishSignIn(); };

  const novncUrl = session ? buildDesktopNoVncUrl(session.base, session.wsPort) : null;
  const surfaceReady = phase === 'live' || phase === 'connecting';

  return (
    <PanelModal
      onClose={onClose}
      // AS BIG AS THE WINDOW ALLOWS (2026-09-03 owner directive: "should take
      // more space on the screen so the user can easily see"). The old
      // min(1100px, 94vw) × a 48vh surface showed a full desktop browser page
      // — chrome, address bar, a login form — inside roughly 1050×550, which
      // is why an Oura one-time-code page arrived unreadable.
      //
      // The height cap and the surface size are safe to raise ONLY because
      // `bodyFill` makes the live view yield to the pinned footer by
      // construction; the 2026-08-23 report (controls below the fold on a
      // short viewport) cannot recur through a bigger number here.
      // A DEFINITE HEIGHT, not just a cap (2026-09-04). The card carried a
      // `maxHeight` and no `height`, which makes a flex column shrink-to-fit:
      // `flex: 1` on the body had no free space to hand out and the live view
      // collapsed to its 240px floor inside a card 1500px wide. That is the
      // owner's "wider but not larger", and it is why the old size toggle —
      // which moved the width (applied) and the maxHeight (inert) — only ever
      // got wider. `maxHeight: '100%'` is the belt against the overlay's own
      // padding. In REAL fullscreen the UA's `:fullscreen` rule sets
      // position/inset/width/height with !important, so the 100% swap is
      // belt; `radius: 0` is load-bearing, because border-radius is NOT in
      // that rule and a rounded card shows the black ::backdrop at each
      // corner. In the CSS-COVER fallback (the API refused) nothing is
      // !important, so 100%/100% plus `flush` IS the cover.
      width={maximised ? '100%' : (roomy ? SIGNIN_CARD_WIDTH_ROOMY : SIGNIN_CARD_WIDTH)}
      height={maximised ? '100%' : (roomy ? SIGNIN_CARD_HEIGHT_ROOMY : SIGNIN_CARD_HEIGHT)}
      maxHeight="100%"
      radius={maximised ? '0' : undefined}
      flush={maximised}
      cardRef={cardRef}
      // NEVER backdrop-dismiss a sign-in (Fable review of #1586). A scrim
      // mis-click has exactly the cost the Escape guard exists to prevent —
      // a half-finished sign-in and its VM session gone. The header × and
      // "I'll finish later" are the deliberate exits.
      dismissable={false}
      // Reserve the footer's tallest shape up front so the access chips, the
      // workspace toggle and an error line, which all arrive AFTER mount,
      // fill space already held instead of shrinking the slot and rescaling
      // the remote screen under the user's cursor mid-password.
      footerMinHeight="103px"
      bodyFill
      zIndex={DESKTOP_SIGNIN_Z_INDEX}
      ariaLabel={`Sign in to ${appName}`}
      testId="desktop-signin-modal"
      footer={(
        <>
      {/* ── Access, one tap (2026-08-18): defaults pre-approved; tapping a
          narrower preset is the whole edit. Only when a custom connection is
          registered (the save target). ── */}
      {customSlug && !compact && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.76rem', color: panelTheme.inkMuted }}>
            Prest0n&rsquo;s access here:
          </span>
          {([['full', 'everything'], ['read-write', 'standard'], ['read-only', 'read-only']] as const).map(([value, label]) => {
            const active = accessKnown && customAccess === value;
            return (
              <button
                key={value}
                onClick={() => void pickCustomAccess(value)}
                disabled={accessSaving}
                data-testid={`signin-access-${value}`}
                style={{
                  fontSize: '0.74rem', fontWeight: 600, padding: '0.22rem 0.6rem',
                  borderRadius: panelTheme.radiusChip,
                  border: active ? `1px solid ${panelTheme.gold}` : '1px solid transparent',
                  background: panelTheme.goldTintStrong, color: panelTheme.goldInk,
                  cursor: accessSaving ? 'wait' : 'pointer', textTransform: 'lowercase',
                  opacity: accessSaving ? 0.6 : 1,
                }}
              >
                {active ? '✓ ' : ''}{label}{value === 'full' ? ' (default)' : ''}
              </button>
            );
          })}
          <span style={{ fontSize: '0.7rem', color: panelTheme.grayFaint }}>
            change anytime in integrations.
          </span>
        </div>
      )}

      {/* ── Completion (user-attested) ── */}
      {workspaceShareTarget && (
        <label
          data-testid="use-on-workspace-toggle"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.45rem', marginTop: '0.9rem',
            fontSize: '0.8rem', color: panelTheme.ink, cursor: 'pointer', textTransform: 'lowercase',
          }}
        >
          <input
            type="checkbox"
            checked={useOnWorkspace}
            onChange={(e) => setUseOnWorkspace(e.target.checked)}
            style={{ accentColor: panelTheme.gold }}
          />
          <span>
            use on this workspace — {workspaceShareTarget.label}
            {!useOnWorkspace && (
              <span style={{ color: panelTheme.inkMuted }}> · saved to my connections only</span>
            )}
          </span>
        </label>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap', marginTop: '0.9rem' }}>
        {customSlug && (
          <input
            type="text"
            value={signedInAs}
            onChange={(e) => setSignedInAs(e.target.value)}
            placeholder="signed in as (email — optional)"
            aria-label="signed in as"
            data-testid="signin-identity-input"
            maxLength={120}
            // display:none, not unmount (Fable review of #1586): a control that
            // vanishes under a focused finger is wrong, and the value is
            // React state either way. Compact only takes the row's height.
            style={{
              display: compact ? 'none' : undefined,
              fontSize: '0.8rem', padding: '0.42rem 0.7rem',
              borderRadius: panelTheme.radiusPill, border: `1px solid ${panelTheme.borderSoft}`,
              background: '#fff', color: panelTheme.ink, minWidth: 220,
            }}
          />
        )}
        {/* ── ONE TAP INSTEAD OF TYPING (2026-09-03 owner directive: the
            identity "needs to be displayed" on both onboarding and the
            integrations page).

            The field above has been optional since 2026-08-18 and is, in
            practice, almost always left empty — so most browser-lane tiles
            carry no identity at all, and a user with two accounts on one app
            cannot tell them apart. Most people sign into an app with the same
            address they signed into Prest0n with, so this offers it as ONE
            TAP.

            It is a suggestion, never a prefill: the value is only recorded
            because the user chose it. Guessing an identity into a connection
            nobody confirmed would be the same class of untruth as a green
            tile for a key nobody checked. ── */}
        {customSlug && !compact && accountEmail && !signedInAs && (
          <button
            onClick={() => setSignedInAs(accountEmail)}
            data-testid="signin-identity-suggest"
            title={accountEmail}
            style={{
              background: 'none', border: `1px solid ${panelTheme.borderSoft}`,
              borderRadius: panelTheme.radiusPill, color: panelTheme.grayText,
              fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.6rem',
              cursor: 'pointer', maxWidth: 240,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {SIGNIN_COPY.useMyEmail[uiLang](accountEmail)}
          </button>
        )}
        <button
          onClick={() => void finishSignIn()}
          disabled={finishing || phase !== 'live'}
          data-testid="signin-done"
          data-verified={verified ? 'true' : 'false'}
          style={{
            padding: '0.55rem 1.2rem', borderRadius: panelTheme.radiusPill,
            border: verified ? `2px solid ${panelTheme.successDeep}` : 'none',
            background: phase === 'live' ? panelTheme.gold : panelTheme.grayBg,
            color: phase === 'live' ? panelTheme.ink : panelTheme.grayFaint,
            fontWeight: 700, fontSize: '0.88rem',
            cursor: finishing || phase !== 'live' ? 'default' : 'pointer',
            textTransform: 'lowercase',
            boxShadow: verified ? `0 0 0 4px ${panelTheme.goldTintStrong}` : undefined,
          }}
        >
          {finishing
            ? 'saving…'
            : verified
              ? "looks like you're in — finish connecting"
              : "done — I'm signed in"}
        </button>
        <button
          onClick={onClose}
          data-testid="signin-later"
          style={{
            background: 'none', border: 'none', color: panelTheme.grayText,
            fontSize: '0.82rem', cursor: 'pointer', textTransform: 'lowercase',
            textDecoration: 'underline',
          }}
        >
          I&rsquo;ll finish later
        </button>
        {finishError && (
          <span style={{ fontSize: '0.78rem', color: panelTheme.danger }}>{finishError}</span>
        )}
      </div>
        </>
      )}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <div style={{ fontWeight: 700, color: panelTheme.ink, fontSize: '1.05rem', textTransform: 'lowercase' }}>
          sign in to {appName}
        </div>
        {!compact && !roomy && (
          <span style={{ fontSize: '0.74rem', color: panelTheme.grayText }}>
            on Prest0n&rsquo;s own browser — your password never touches Prest0n
          </span>
        )}
        {/* Hidden outright where the API does not exist (iOS Safari has no
            Element.requestFullscreen for anything but video). The /desktop
            page's rule, verbatim: a button that does nothing is worse than
            no button. The CSS cover stays for the API-present-but-refused
            path, where the button still does something real. */}
        {fullscreenSupported && (
        <button
          onClick={() => { if (maximised) void exitFullscreen(); else void enterFullscreen(); }}
          data-testid="signin-fullscreen"
          aria-pressed={maximised}
          title={maximised ? SIGNIN_COPY.exitFullscreen[uiLang]
            : (compact ? SIGNIN_COPY.moreRoom[uiLang] : SIGNIN_COPY.fullscreen[uiLang])}
          style={{
            marginLeft: 'auto', background: 'none', border: `1px solid ${panelTheme.borderSoft}`,
            borderRadius: panelTheme.radiusChip, cursor: 'pointer',
            color: panelTheme.grayText, fontSize: '0.72rem', fontWeight: 600,
            padding: '0.16rem 0.5rem', textTransform: 'lowercase', whiteSpace: 'nowrap',
          }}
        >
          {maximised ? SIGNIN_COPY.exitFullscreen[uiLang]
            : (compact ? SIGNIN_COPY.moreRoom[uiLang] : SIGNIN_COPY.fullscreen[uiLang])}
        </button>
        )}
        <button
          onClick={onClose}
          aria-label="close"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: panelTheme.grayFaint, fontSize: '1.15rem', lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* ── PHONE: the own window is the primary action ──
          A conditional in a FIXED position before the slot, so its arrival
          or departure never changes the slot's index among its siblings —
          the iframe inside the stage must never remount (see the stage's own
          comment). Rendered only in phone posture; the grey own-window button
          below the live view stays as well, so nothing anyone is used to
          moves. ── */}
      {phone && surfaceReady && session ? (
        <div
          data-testid="signin-own-window-primary"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
            padding: '0.55rem 0.75rem', marginBottom: '0.5rem',
            background: panelTheme.surface, border: `1px solid ${panelTheme.gold}`,
            borderRadius: panelTheme.radiusCard,
          }}
        >
          <span style={{ fontSize: '0.8rem', color: panelTheme.ink, flex: 1, minWidth: 160 }}>
            {SIGNIN_COPY.phoneOwnWindow[uiLang]}
          </span>
          <button
            onClick={() => void openLiveDesktop()}
            disabled={minting}
            data-testid="signin-own-window-primary-cta"
            style={{
              padding: '0.5rem 1rem', borderRadius: panelTheme.radiusPill, border: 'none',
              background: panelTheme.gold, color: panelTheme.ink, fontWeight: 700,
              fontSize: '0.86rem', cursor: minting ? 'wait' : 'pointer', textTransform: 'lowercase',
              whiteSpace: 'nowrap',
            }}
          >
            {minting ? 'opening…' : 'open the live desktop'}
          </button>
        </div>
      ) : null}

      {/* ── The slot: the matting the stage is centred in ──
          FLEX, not a vh clamp (2026-09-03). The clamp existed because the
          body scrolled: any height the surface took was height the done/later
          controls could be pushed below (the 2026-08-23 owner report). Under
          `bodyFill` the body does not scroll, so `flex: 1` means exactly
          "everything left after the header and the pinned footer".

          NO `minHeight` FLOOR ANY MORE (2026-09-04). A 240px floor is what
          set the height of a shrink-to-fit card, and against a card with a
          real height it could only overflow. The slot takes what the card
          gives it; the stage takes the slot. `flex: 1` is basis-zero, so the
          slot's size contains no term from the stage — which is what makes
          useStageFit's ResizeObserver provably loop-free. ── */}
      <div
        ref={slotRef}
        data-testid="signin-surface"
        style={{
          flex: 1, minHeight: 0, width: '100%', overflow: 'hidden',
          display: 'flex',
          // Top-align when the slot is genuinely short: login forms live at
          // the top of a page, so if anything must fall outside it should be
          // the bottom of the remote screen, never the username field.
          alignItems: fit && fit.height < 200 ? 'flex-start' : 'center',
          justifyContent: 'center',
          background: panelTheme.surface, border: `1px solid ${panelTheme.borderSoft}`,
          borderRadius: panelTheme.radiusCard,
        }}
      >
      {/* ── The stage: EXACTLY the remote framebuffer's aspect ──
          The screen on the other end is an immutable 1280x800 and the viewer
          is asked to `resize=scale`, which preserves aspect. So any box whose
          shape differs letterboxes, and the bars are noVNC's own #313131 —
          not our warm surface, which sits behind the iframe. Give the stage
          the screen's own shape and the scale fills it edge to edge: there is
          nothing left to letterbox. That beats the /desktop page's measure-
          and-paint-over-the-bars approach, which is unavailable here anyway
          (that page is served BY the VM and is same-origin with the viewer;
          this iframe is cross-origin and cannot be read or restyled).

          This element and the slot are UNCONDITIONAL and only their styles
          change. Wrapping either in a conditional, or keying it off the
          fullscreen flag, would move the iframe in the React tree — React
          would unmount and remount it, and the VNC session would reconnect
          mid-password. ── */}
        <div
          data-testid="signin-stage"
          data-stage-w={fit ? String(fit.width) : ''}
          data-stage-h={fit ? String(fit.height) : ''}
          style={{
            position: 'relative', flex: 'none', overflow: 'hidden',
            width: fit ? fit.width : '100%',
            height: fit ? fit.height : '100%',
            maxWidth: '100%', maxHeight: '100%',
            background: panelTheme.surface, borderRadius: panelTheme.radiusCard,
          }}
        >
        {/* Interactive noVNC view — the VM serves the page; type right here. */}
        {surfaceReady && session && novncUrl && (
          <iframe
            key={session.sessionId}
            src={novncUrl}
            title={`${appName} sign-in (Prest0n's browser)`}
            data-testid="signin-novnc-frame"
            onLoad={() => setPhase((prev) => (prev === 'connecting' ? 'live' : prev))}
            allow="clipboard-read; clipboard-write"
            style={{
              // ONE PIXEL OF OVERSCAN, and no width/height of its own: the
              // iframe stretches 2px past the stage in each axis and the
              // stage clips it, so a sub-pixel rounding mismatch costs one
              // row of REMOTE pixels instead of showing a dark seam. Same
              // doctrine as the /desktop page's band panels.
              // `inset` ALONE IS NOT ENOUGH: an <iframe> carries width/height
              // content attributes defaulting to 300x150, which map to
              // presentational hints. Those set a definite width, so the box
              // is over-constrained and `right`/`bottom` are ignored — the
              // frame renders 300x150 inside the stage. The explicit calc()
              // beats the hint and keeps the 1px of overscan on every side.
              position: 'absolute', inset: '-1px',
              width: 'calc(100% + 2px)', height: 'calc(100% + 2px)',
              border: 'none', background: '#fff',
            }}
          />
        )}

        {/* ── PASTE FROM YOUR COMPUTER (2026-09-03 owner directive) ──
            A one-time code lives in the user's mail app, on THEIR machine.
            The window below is a browser on another machine, so ⌘V in it
            pastes that machine's empty clipboard. This chip carries theirs
            across; their own ⌘V then works, and nothing is ever typed for
            them (the co-drive interlock's promise is untouched — see the
            /clipboard route's header).

            It sits ON the surface, at the corner nearest the address bar,
            because that is where the eye already is when the page asks for
            a code. Reading the clipboard happens INSIDE this click, which
            is what makes the browser willing to grant it at all. */}
        {phase === 'live' && session && novncUrl && (
          <div
            style={{
              position: 'absolute', top: 8, right: 8, zIndex: 2,
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              maxWidth: 'calc(100% - 16px)',
            }}
          >
            {pasteState !== 'idle' && pasteState !== 'busy' && (
              <span
                data-testid="signin-paste-note"
                style={{
                  fontSize: '0.72rem', fontWeight: 600, borderRadius: panelTheme.radiusChip,
                  padding: '0.2rem 0.5rem', maxWidth: 360,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  background: 'rgba(255,255,255,0.94)',
                  color: pasteState === 'ready' ? panelTheme.successDeep : panelTheme.inkMuted,
                  border: `1px solid ${panelTheme.borderSoft}`,
                }}
              >
                {pasteState === 'ready'
                  ? SIGNIN_COPY.pasteReady[uiLang]
                  : pasteState === 'empty'
                    ? SIGNIN_COPY.pasteEmpty[uiLang]
                    : pasteState === 'denied'
                      ? SIGNIN_COPY.pasteDenied[uiLang]
                      : SIGNIN_COPY.pasteFailed[uiLang]}
              </span>
            )}
            <button
              onClick={() => void pasteIntoWindow()}
              disabled={pasteState === 'busy'}
              data-testid="signin-paste"
              style={{
                background: 'rgba(255,255,255,0.94)', border: `1px solid ${panelTheme.border}`,
                borderRadius: panelTheme.radiusChip, color: panelTheme.ink,
                fontSize: '0.74rem', fontWeight: 700, padding: '0.24rem 0.62rem',
                cursor: pasteState === 'busy' ? 'default' : 'pointer',
                textTransform: 'lowercase', whiteSpace: 'nowrap', flex: 'none',
              }}
            >
              {pasteState === 'busy' ? SIGNIN_COPY.pasteBusy[uiLang] : SIGNIN_COPY.paste[uiLang]}
            </button>
          </div>
        )}

        {/* Human-step guidance OVER the live view (2026-08-20 owner directive):
            when the journey hits a captcha / 2FA / credential wall, the
            computer-use plane composed WHAT to say, WHERE, and whether to
            point — rendered here as an absolute sibling of the boot/error
            cards, above the iframe, cleared the moment prest0n can resume. */}
        {phase === 'live' && guidance && (
          <DesktopGuidanceOverlay guidance={guidance} onDismiss={() => setGuidance(null)} />
        )}

        {/* Session alive but no embedded view possible (old service build):
            the own-window button below is the interactive surface. */}
        {surfaceReady && session && !novncUrl && (
          <div
            data-testid="signin-own-window-hint"
            style={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              color: panelTheme.inkMuted, fontSize: '0.9rem', textAlign: 'center', padding: '1rem',
            }}
          >
            <div>
              <div style={{ fontSize: '1.4rem', marginBottom: 8 }} aria-hidden="true">🖥️</div>
              Prest0n&rsquo;s browser is ready — use &ldquo;open the live desktop&rdquo; below to sign in.
            </div>
          </div>
        )}

        {phase === 'connecting' && session && novncUrl && (
          <div
            data-testid="signin-boot-status"
            style={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              padding: '0.4rem 0.75rem', fontSize: '0.78rem',
              color: panelTheme.inkMuted, background: 'rgba(255,255,255,0.85)',
            }}
          >
            connecting the live view…
          </div>
        )}

        {phase === 'starting' && (
          <div
            data-testid="signin-boot-status"
            style={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              color: panelTheme.inkMuted, fontSize: '0.9rem', textAlign: 'center', padding: '1rem',
            }}
          >
            <div>
              <div style={{ fontSize: '1.4rem', marginBottom: 8 }} aria-hidden="true">🖥️</div>
              starting Prest0n&rsquo;s secure browser… just a few seconds.
            </div>
          </div>
        )}

        {/* Wake/provision progress (2026-08-19): the same just-in-time
            lifecycle copy /desktop uses — honest about the wait, no jargon. */}
        {phase === 'waking' && (
          <div
            data-testid="signin-waking"
            style={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              color: panelTheme.inkMuted, fontSize: '0.9rem', textAlign: 'center', padding: '1rem',
            }}
          >
            <div>
              <div style={{ fontSize: '1.4rem', marginBottom: 8 }} aria-hidden="true">
                {bootProgress?.stage === 'provisioning' ? '🛠️' : '⏰'}
              </div>
              {bootProgress?.stage === 'provisioning' ? (
                <>setting up Prest0n&rsquo;s computer for your workspace — first time only,
                usually a couple of minutes. the sign-in screen opens the moment it&rsquo;s ready.</>
              ) : (
                <>waking Prest0n&rsquo;s computer — it dozed off after a quiet stretch.
                usually {bootProgress?.etaSeconds ? `~${Math.max(1, Math.ceil(bootProgress.etaSeconds / 60))} minute${bootProgress.etaSeconds > 90 ? 's' : ''}` : 'a minute or two'}.</>
              )}
            </div>
          </div>
        )}

        {/* Terminal gate outcomes — each says what is actually wrong. */}
        {phase === 'blocked' && (
          <div
            data-testid={`signin-blocked-${blocked?.outcome || 'unknown'}`}
            style={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              color: panelTheme.inkMuted, fontSize: '0.9rem', textAlign: 'center', padding: '1rem',
            }}
          >
            <div>
              <div style={{ marginBottom: 10 }}>
                {blocked?.outcome === 'no-workspace' && noWorkspaceRetries < NO_WORKSPACE_AUTO_RETRIES && (
                  // Still inside the auto-heal window: a fresh signup's org
                  // usually settles in well under a minute — say so instead
                  // of sending the user out of the setup they are inside.
                  <>getting your workspace computer ready — this usually takes
                  under a minute. it&rsquo;ll connect here automatically.</>
                )}
                {blocked?.outcome === 'no-workspace' && noWorkspaceRetries >= NO_WORKSPACE_AUTO_RETRIES && (
                  <>your workspace computer isn&rsquo;t set up yet — chat still works.
                  finish workspace setup (or ask your workspace owner), then come back here.</>
                )}
                {blocked?.outcome === 'billing-paused' && (
                  <>Prest0n&rsquo;s computer is paused while billing is paused —
                  reactivating the subscription wakes it automatically.</>
                )}
                {(blocked?.outcome === 'stalled' || blocked?.outcome === 'unreachable') && (
                  <>Prest0n&rsquo;s computer is taking longer than usual to come up.
                  nothing was lost — you can try again, or watch it on the desktop page.</>
                )}
              </div>
              {(blocked?.outcome === 'stalled' || blocked?.outcome === 'unreachable' || blocked?.outcome === 'no-workspace') && (
                <button
                  onClick={restart}
                  data-testid="signin-retry"
                  style={{
                    padding: '0.45rem 1rem', borderRadius: panelTheme.radiusPill, border: 'none',
                    background: panelTheme.gold, color: panelTheme.ink, fontWeight: 700,
                    fontSize: '0.84rem', cursor: 'pointer', textTransform: 'lowercase',
                  }}
                >
                  try again
                </button>
              )}
            </div>
          </div>
        )}

        {phase === 'unreachable' && (
          <div
            data-testid="signin-unreachable"
            style={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              color: panelTheme.inkMuted, fontSize: '0.9rem', textAlign: 'center', padding: '1rem',
            }}
          >
            <div>
              <div style={{ marginBottom: 10 }}>
                Prest0n&rsquo;s browser isn&rsquo;t reachable right now — nothing was lost.
              </div>
              <button
                onClick={restart}
                data-testid="signin-retry"
                style={{
                  padding: '0.45rem 1rem', borderRadius: panelTheme.radiusPill, border: 'none',
                  background: panelTheme.gold, color: panelTheme.ink, fontWeight: 700,
                  fontSize: '0.84rem', cursor: 'pointer', textTransform: 'lowercase',
                }}
              >
                try again
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* ── Own-window option + status row ──
          ALWAYS MOUNTED (Fable review of #1586, 2026-09-04). The first cut
          hid this whole row in compact chrome on the theory that the header's
          fullscreen control was "the escape hatch". It is not: fullscreen and
          the OWN-WINDOW path are different things, and on a phone the own
          window is the only path that gives the remote screen a usable size.
          Hiding the row also removed the pause-Prest0n control and every
          pilot failure line ("destination is dead", "no destination"), and
          left the old-build hint at signin-own-window-hint pointing at a
          button that no longer existed — a hard dead end. In compact only
          the explanatory label goes; every control and every verdict stays. ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginTop: (compact || roomy) ? '0.35rem' : '0.6rem' }}>
        {surfaceReady && session && (
          <>
            {!compact && !roomy && !phone && (
              <span style={{ fontSize: '0.8rem', color: panelTheme.inkMuted }}>
                {novncUrl ? 'prefer its own window?' : 'sign in here:'}
              </span>
            )}
            <button
              onClick={() => void openLiveDesktop()}
              disabled={minting}
              data-testid="open-live-desktop"
              style={{
                padding: '0.45rem 1rem', borderRadius: panelTheme.radiusPill, border: 'none',
                background: panelTheme.ink, color: '#fff', fontWeight: 700,
                fontSize: '0.82rem', cursor: minting ? 'wait' : 'pointer', textTransform: 'lowercase',
              }}
            >
              {minting ? 'opening…' : 'open the live desktop'}
            </button>
          </>
        )}
        {surfaceReady && pilot === 'driving' && (
          <span data-testid="signin-pilot-status" style={{ fontSize: '0.78rem', color: panelTheme.goldInk }}>
            🧭 Prest0n is driving to the sign-in page…
          </span>
        )}
        {surfaceReady && pilot === 'arrived' && (
          <span data-testid="signin-pilot-arrived" style={{ fontSize: '0.78rem', color: panelTheme.successDeep }}>
            ✓ sign-in page ready — enter your details in the window
          </span>
        )}
        {surfaceReady && pilot === 'manual' && (
          <span data-testid="signin-pilot-manual" style={{ fontSize: '0.78rem', color: panelTheme.goldInk }}>
            🧭 {PILOT_MANUAL_COPY[lang ?? browserLang()]}
          </span>
        )}
        {/* The destination itself is dead (404/410 or a not-found page): the
            drive is not the problem, the address is — and Prest0n has already
            told the server so and keeps looking. Code-authored FAILURE copy
            (D9), {en, es}. */}
        {surfaceReady && pilot === 'notfound' && (
          <span data-testid="signin-pilot-notfound" style={{ fontSize: '0.78rem', color: panelTheme.goldInk }}>
            🧭 {PILOT_NOTFOUND_COPY[lang ?? browserLang()]}
          </span>
        )}
        {/* No destination at all — the guess rung is retired (C8) and this
            case used to render nothing. Code-authored FAILURE copy (D9). */}
        {surfaceReady && pilot === 'nodestination' && (
          <span data-testid="signin-pilot-nodestination" style={{ fontSize: '0.78rem', color: panelTheme.goldInk }}>
            🧭 {PILOT_NO_DESTINATION_COPY[lang ?? browserLang()]}
          </span>
        )}
        {surfaceReady && session && journeyLive && (
          <button
            onClick={() => void toggleJourneyPause()}
            disabled={journeyControlPending}
            data-testid="signin-journey-pause"
            data-control={journeyDoc?.control ?? ''}
            title={journeyPaused
              ? 'Prest0n picks things back up from wherever you are'
              : 'Prest0n stops driving until you resume — you can act freely'}
            style={{
              marginLeft: 'auto', padding: '0.3rem 0.75rem', borderRadius: panelTheme.radiusPill,
              border: `1px solid ${panelTheme.borderSoft}`, background: 'none',
              color: panelTheme.inkMuted, fontSize: '0.76rem', fontWeight: 600,
              cursor: journeyControlPending ? 'wait' : 'pointer',
            }}
          >
            {journeyControlPending ? '…' : journeyPaused ? '▶ let Prest0n continue' : '⏸ pause Prest0n'}
          </button>
        )}
      </div>

    </PanelModal>
  );
};

export default DesktopSignInModal;
